<template>
  <div :class="`w-100 h-100 ${flush ? '' : 'p-md-3'} d-flex flex-column bg-light position-relative overflow-hidden`">
    <div class="d-flex flex-row align-items-stretch h-100">

      <!-- Slot: Left Side Bar -->
      <div v-if="$slots['left-bar']" :class="['map-left-bar border-start h-100', {open:showSideBar && !streetViewAddress}]">
        <div class="map-left-bar-content d-flex flex-column overflow-auto">
          <slot name="left-bar" />
        </div>
        <div
          v-if="!streetViewAddress && !disableLeftBarToggle"
          class="map-sidebar-toggle d-flex flex-column justify-content-center align-items-center overflow-hidden">
          <icon-button
            class="h-100"
            :icon="showSideBar ? 'caret-left-fill' : 'caret-right-fill'"
            @click="showSideBar = !showSideBar"/>
        </div>
      </div>

      <!-- Slot: Street View Bar -->
      <div class="position-absolute bottom-0 start-0" style="z-index:9999" v-if="$slots['street-view'] && !!streetViewAddress">
        <slot name="street-view" :address="streetViewAddress" />
      </div>

      <GoogleMap
        ref="mapRef"
        api-key="AIzaSyDOqYud38oaZms1EpdoLsxoBA63oDD-wes"
        :class="`${flush ? '' : 'rounded overflow-hidden'} flex-grow-1`"
        @maptypeid_changed="mapTypeChanged"
        @zoom_changed="zoomChanged"
        @click="clearSelection"
        :map-type-id="LocalStorage.get('mapTypeId') || 'satellite'"
        :fullscreen-control="false"
        :disable-default-ui="hideButtons"
        :disable-double-click-zoom="hideButtons"
        :draggable="!hideButtons"
        :clickable-icons="!hideButtons"
        :styles="[{featureType:'poi',elementType:'labels',stylers:[{visibility:'off'}]}]"
        :center="center"
        :zoom="zoom">
        <template #default="{map, api}">
          {{ setMap(map, api) }}

          <template v-for="item in items" :key="item.id">
            <slot name="marker" :item="item" />
          </template>

          <Polygon
            v-for="(boundary, i) in boundaries"
            :key="`boundary_${i}`"
            :options="boundary"
          />
          <Circle
            v-if="radiusOptions"
            :options="radiusOptions"
            @center_changed="radiusChanged"
            @radius_changed="radiusChanged"
            ref="circle"
          />

          <CustomControl position="TOP_LEFT" v-if="$slots['top-left']">
            <slot name="top-left" />
          </CustomControl>

          <InfoWindow
            v-if="$slots['selected-feature'] && !!selectedFeature"
            :options="{position:positionForFeature(selectedFeature)}"
            v-model="selectedFeatureWindowOpen">
            <div style="max-width:20vw; width:432px">
              <slot name="selected-feature" :feature="selectedFeature" />
            </div>
          </InfoWindow>
        </template>
      </GoogleMap>
    </div>
    <!-- Mobile Only -->
    <div v-if="!!$slots.info && !!selected" class="border-top w-100 bg-white d-block d-md-none d-flex flex-column align-items-stretch">
      <div class="d-flex flex-column align-items-end py-2 px-3 border-bottom">
        <button type="button" class="btn btn-close btn-flat" @click="$emit('deselect')"/>
      </div>
      <div class="w-100 p-3">
        <slot name="info" :data="selected" />
      </div>
    </div>
  </div>
</template>

<script>
import { GoogleMap, Polygon, Circle, CustomControl, InfoWindow } from 'vue3-google-map';
import {City} from "@/models/City";
import IconButton from "@/components/common/IconButton.vue";
import GeoJsonPropertyFeature from "@/models/GeoJsonPropertyFeature";
export default {
  emits: ['deselect'],
  components: {
    IconButton,
    GoogleMap,
    Polygon,
    Circle,
    CustomControl,
    InfoWindow
  },
  props: {
    city: City,
    selectedMarkerId: String,
    radius: Number,
    items: Array,
    flush: {
      type: Boolean,
      default: () => true
    },
    initialZoom: Number,
    hideButtons: Boolean,
    showPropertyBoundaries: Boolean,
    minZoomToShowPropertyBoundaries: {
      type: Number,
      default: () => 16
    },
    disableLeftBarToggle: Boolean
  },
  data() {
    return {
      center: (this.city && this.city.latlng) || { lat: 43.5428189, lng: -70.32978830000002 },
      zoom: this.initialZoom || 12,
      map: null,
      isLoaded: false,
      isGeoJsonLoaded: false,
      streetViewAddress: null,
      selectedFeature: null,
      showSideBar: true,
      dataLayerListeners: {
        mouseover: event => {
          event.feature.setProperty('hover', true);
          // this.map.data.overrideStyle(event.feature, {fillOpacity: 0.5});
        },
        mouseout: event => {
          event.feature.setProperty('hover', false);
          // this.map.data.revertStyle(event.feature, {fillOpacity: 0.5});
        },
        click: event => {
          const oldId = this.selectedFeature?.getId();
          const newId = event.feature?.getId();
          if (newId === oldId) {
            console.info("Deselecting map feature");
            this.selectedFeature = null;
          } else {
            console.info("Changing map selection");
            this.selectedFeature = event.feature;
          }
        }
      }
    };
  },
  mounted() {
    setTimeout(() => {
      this.isLoaded = true;
    }, 10000);
  },
  watch: {
    city() {
      if (this.city && this.city.latlng) {
        this.center = this.city.latlng;
      }
    },
    selectedMarkerId(newValue, oldValue) {
      // Only adjust zoom and center if changed
      if (newValue !== oldValue) {
        this.closeStreetView();
        this.selectedFeature = null;
        if (this.selectedLatLng) {
          this.center = this.selectedLatLng;
        }
      }
    },
    zoom() {
      this.applyPropertyBoundaryStyles();
    },
    selectedFeature(newFeature, oldFeature) {
      oldFeature?.setProperty('click', false);
      newFeature?.setProperty('click', true);
    },
    showPropertyBoundaries() {
      this.loadPropertyBoundariesIfNeeded();
      this.applyPropertyBoundaryStyles();
    },
    map() {
      if (this.map) {
        this.loadPropertyBoundariesIfNeeded();

        for (let eventName in this.dataLayerListeners) {
          // this.map.data.removeListener(eventName, this.dataLayerListeners[eventName]);
          this.map.data.addListener(eventName, this.dataLayerListeners[eventName]);
        }

        let streetViewListener = () => {
          let sv = this.map.getStreetView();
          let svLocation = sv.getLocation();
          if (sv.visible && !!svLocation) {
            this.streetViewAddress = svLocation.shortDescription;
          } else {
            this.streetViewAddress = null;
          }
        };
        this.map.getStreetView().addListener('pano_changed', streetViewListener);
        this.map.getStreetView().addListener('position_changed', streetViewListener);
        this.map.getStreetView().addListener('pov_changed', streetViewListener);
        this.map.getStreetView().addListener('visible_changed', streetViewListener);
      }
    }
  },
  computed: {
    selectedLatLng() {
      return this.selected ? this.selected.latlng : null;
    },
    selected() {
      return this.itemIndex[this.selectedMarkerId];
    },
    itemIndex() {
      let index = {};
      this.items?.forEach(item => index[item.id] = item);
      return index;
    },
    boundaries() {
      if (this.city) {
        const geoJson = this.city.geoJson;
        if (geoJson) {
          // let color = '#' + (this.city.backgroundColor || '4171d9');
          let coords = geoJson.coordinates;
          if (typeof coords[0][0][0] === 'number') {
            coords = [coords];
          }
          return coords.map(coords2 => {
            return {
              paths: coords2.map(path => {
                return path.map(coord => {
                  return {lat: coord[1], lng: coord[0]};
                })
              }),
              fillOpacity: 0.0,
              // fillColor: '4171d922',
              strokeColor: '#4171d9cc',
              strokeWeight: 4
            };
          });
        }
      }
      return [];
    },
    radiusOptions() {
      const radius = 0; // 200 feet
      if (radius > 0) {
        return {
          center: this.center,
          radius: radius,
          editable: true,
          fillOpacity: 0.2,
          fillColor: '#4171d9',
          strokeColor: '#4171d9',
          strokeWeight: 3
        };
      } else {
        return null;
      }
    },
    selectedFeatureWindowOpen: {
      get() {
        return Boolean(this.selectedFeature);
      },
      set(open) {
        if (!open) {
          this.selectedFeature = null;
        }
      }
    },
  },
  methods: {
    applyPropertyBoundaryStyles() {
      if (this.map) {
        if (this.zoom >= this.minZoomToShowPropertyBoundaries && this.showPropertyBoundaries) {
          this.map.data.setStyle(feature => this.applyMapFeatureStyle(feature));
        } else {
          this.map.data.setStyle({visible:false});
        }
      }
    },
    applyMapFeatureStyle(feature) {
      let isHover = feature.getProperty('hover');
      if (isHover !== true && isHover !== false) {
        feature.setProperty('hover', false);
      }
      let isClick = feature.getProperty('click');
      if (isClick !== true && isClick !== false) {
        feature.setProperty('click', false);
      }
      const type = feature.getProperty('type') || feature.getProperty('TYPE');
      const propertyId = GeoJsonPropertyFeature.getPropertyIdFromFeature(feature);
      return {
        strokeColor: '#4171d9',
        strokeWeight: isClick ? 3 : 1,
        fillColor: '#4171d9',
        fillOpacity: isClick || isHover ? 0.5 : 0,
        zIndex: 101,
        visible: (!type || type === 'Parcel') && Boolean(propertyId)
      };
    },
    loadPropertyBoundariesIfNeeded() {
      if (this.showPropertyBoundaries && this.map && !this.isGeoJsonLoaded) {
        const propertyBoundariesUrl = this.city?.propertyBoundariesUrl;
        if (propertyBoundariesUrl && !this.isGeoJsonLoaded) {
          this.map.data.loadGeoJson(propertyBoundariesUrl);
          this.applyPropertyBoundaryStyles();
          this.isGeoJsonLoaded = true;
        }
      }
    },
    setMap(map, api) {
      this.map = map;

      // Reposition the map-type control because the prop doesn't work
      if (map && api) {
        map.setOptions({
          mapTypeControlOptions: {
            position: api.ControlPosition.TOP_RIGHT
          },
          zoomControl: true,
          cameraControl: false,
        });
      }
      return '';
    },
    mapTypeChanged() {
      if (this.map) {
        const mapTypeId = this.map.mapTypeId;
        this.LocalStorage.set('mapTypeId', mapTypeId);
      }
    },
    zoomChanged() {
      if (this.map) {
        this.zoom = this.map.zoom;
      }
    },
    radiusChanged() {

    },
    clearSelection() {
      this.$emit('deselect');
    },
    setZoom(zoom) {
      if (this.map) {
        this.map.setZoom(zoom);
      }
    },
    setCenter(latlng, zoom) {
      if (this.map) {
        this.map.setCenter(latlng);
        if (zoom) {
          this.map.setZoom(zoom);
        }
      }
    },
    openStreetView(latlng) {
      if (latlng && this.map) {
        let svp = this.map.getStreetView();
        svp.setVisible(true);
        svp.setPosition(latlng);
        return true;
      } else {
        return false;
      }
    },
    closeStreetView() {
      if (this.map) {
        this.map.getStreetView().setVisible(false);
      }
    },
    positionForFeature(feature) {
      let latMin = null, latMax = null, lngMin = null, lngMax = null;
      feature.getGeometry().forEachLatLng(latlng => {
        const lat = latlng.lat();
        const lng = latlng.lng();
        if (!latMin || lat < latMin) latMin = lat;
        if (!latMax || lat > latMax) latMax = lat;
        if (!lngMin || lng < lngMin) lngMin = lng;
        if (!lngMax || lng > lngMax) lngMax = lng;
      });
      return {
        lat: (latMin + latMax) / 2,
        lng: (lngMin + lngMax) / 2,
      };
    }
  }
}
</script>

<style>
.map-left-bar {
  position:relative;
  max-width: 33%;
  width: 432px;
  margin-left: -432px;
  transition: margin-left 250ms;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background-color: var(--bs-white);
}
.map-left-bar.open {
  margin-left: 0;
}
.map-left-bar.streetview {
  justify-content: end;
}
.map-left-bar.open.streetview {
  top: unset;
  bottom: 10px;
  max-height: calc(100% - 84px);
}
.map-left-bar .map-left-bar-content {
  background-color: #fff;
  max-height: 100%;
}
.gm-style {
  font: inherit !important;
}
.gm-style .gm-style-iw {
  font-size: inherit !important;
  font-weight: inherit !important;
}
.map-left-bar .map-sidebar-toggle {
  position: absolute;
  border-radius: 0 4px 4px 0;
  width: 24px;
  left: 100%;
  top: 50%;
  margin-top: -19px;
  z-index: 1;
  border-left: 1px solid var(--bs-border-color);
  background-color: var(--bs-white);
}
</style>
